/**
 * MapMeeting.tsx
 * Inbäddad LiveKit-videomöte direkt i kartan — ingen iframe.
 * Visar mikrofon/hörlur/kamera-knappar längst ner i kartvyn.
 * Egna kameran visas som liten förhandsgranskning nere till höger.
 * Klick på deltagare i listen visar deras video.
 * När jaktledare slår på kamera → split-screen (karta ovan, video nedan).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  RemoteParticipant,
  ConnectionState,
} from 'livekit-client';

// Create a simple in-browser logger for Qvenari
const qvenariLogger = {
  info: (context: string, data: Record<string, any>, msg?: string) => {
    console.log(`[QVENARI-MEET] ${context}${msg ? ' — ' + msg : ''}`, data);
  },
  warn: (context: string, data: Record<string, any>, msg?: string) => {
    console.warn(`[QVENARI-MEET] ${context}${msg ? ' — ' + msg : ''}`, data);
  },
  error: (context: string, data: Record<string, any>, msg?: string) => {
    console.error(`[QVENARI-MEET] ${context}${msg ? ' — ' + msg : ''}`, data);
  },
  debug: (context: string, data: Record<string, any>, msg?: string) => {
    console.debug(`[QVENARI-MEET] ${context}${msg ? ' — ' + msg : ''}`, data);
  },
};

export interface ActiveMeeting {
  token: string;
  livekitUrl: string;
  title: string;
  isLeader?: boolean;
}

interface ParticipantState {
  identity: string;
  name: string;
  micOn: boolean;
  camOn: boolean;
}

const T = {
  bg: '#2a302a',
  surface: '#343b34',
  amber: '#EE8F09',
  text: '#e8e8dc',
  textSub: '#9aa896',
  forest: '#4E594C',
  danger: '#E53935',
  green: '#61D836',
};

interface MapMeetingProps {
  meeting: ActiveMeeting;
  fullscreen: boolean;
  onEnd: () => void;
  onLeaderVideo: (hasVideo: boolean) => void;
}

export function MapMeeting({ meeting, fullscreen, onEnd, onLeaderVideo }: MapMeetingProps) {
  const roomRef = useRef<Room | null>(null);
  const [connected, setConnected] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [camEnabled, setCamEnabled] = useState(false);
  const camTogglingRef = useRef(false); // guard against rapid toggle
  const [participants, setParticipants] = useState<Map<string, ParticipantState>>(new Map());
  const [selectedIdent, setSelectedIdent] = useState<string | null>(null);
  const [leaderHasVideo, setLeaderHasVideo] = useState(false);

  // selfVideoElemRef holds the actual DOM element (used for detach on cam-off)
  const selfVideoElemRef = useRef<HTMLVideoElement | null>(null);
  // selfVideoRef is a callback-ref: fires the INSTANT React mounts the <video> element,
  // at which point we attach the LiveKit track immediately — no setTimeout race condition.
  const selfVideoRef = useCallback((el: HTMLVideoElement | null) => {
    selfVideoElemRef.current = el;
    if (!el || !roomRef.current) return;
    const pub = roomRef.current.localParticipant.getTrackPublication(Track.Source.Camera);
    if (pub?.track) {
      console.log('[QVENARI-MEET] callback-ref — video element mounted, attaching camera track', { trackSid: pub.trackSid });
      pub.track.detach(el);
      pub.track.attach(el);
    }
  }, []);

  const leaderVideoRef = useRef<HTMLVideoElement>(null);
  const selectedVideoRef = useRef<HTMLVideoElement>(null);
  const audioElemsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const speakerMutedRef = useRef(false);

  // ── Room lifecycle ──────────────────────────────────────────────
  useEffect(() => {
    qvenariLogger.info('Room lifecycle init', {
      livekitUrl: meeting.livekitUrl,
      meetingTitle: meeting.title,
      tokenLength: meeting.token?.length,
      isLeader: meeting.isLeader,
    }, 'Initializing LiveKit Room');

    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;

    function syncParticipants() {
      const map = new Map<string, ParticipantState>();
      room.remoteParticipants.forEach(p => {
        const mic = p.getTrackPublication(Track.Source.Microphone);
        const cam = p.getTrackPublication(Track.Source.Camera);
        const camOn = !!(cam?.isSubscribed && cam.track && !cam.isMuted);
        map.set(p.identity, {
          identity: p.identity,
          name: p.name || p.identity,
          micOn: mic ? !mic.isMuted : false,
          camOn,
        });
      });
      setParticipants(new Map(map));

      const anyVideo = Array.from(map.values()).some(p => p.camOn);
      setLeaderHasVideo(anyVideo);
      onLeaderVideo(anyVideo);

      qvenariLogger.debug('Participant sync', {
        participantCount: map.size,
        participants: Array.from(map.values()).map(p => ({
          name: p.name,
          micOn: p.micOn,
          camOn: p.camOn,
        })),
        anyLeaderVideo: anyVideo,
      }, 'Remote participants state synchronized');
    }

    room.on(RoomEvent.Connected, () => {
      qvenariLogger.info('RoomEvent.Connected', {
        livekitUrl: meeting.livekitUrl,
        meetingTitle: meeting.title,
        connectionState: room.state,
        remoteParticipantCount: room.remoteParticipants.size,
      }, 'Connected to LiveKit room');
      setConnected(true);
    });

    room.on(RoomEvent.Disconnected, () => {
      qvenariLogger.warn('RoomEvent.Disconnected', {
        meetingTitle: meeting.title,
      }, 'Disconnected from LiveKit room');
      setConnected(false);
    });

    room.on(RoomEvent.Reconnecting, () => {
      qvenariLogger.warn('RoomEvent.Reconnecting', {
        meetingTitle: meeting.title,
      }, 'Attempting to reconnect to LiveKit room');
    });

    room.on(RoomEvent.Reconnected, () => {
      qvenariLogger.info('RoomEvent.Reconnected', {
        meetingTitle: meeting.title,
        connectionState: room.state,
      }, 'Successfully reconnected to LiveKit room');
    });

    room.on(RoomEvent.ParticipantConnected, (participant) => {
      qvenariLogger.info('RoomEvent.ParticipantConnected', {
        participantIdentity: participant.identity,
        participantName: participant.name,
      }, 'Participant joined the hunt meeting');
      syncParticipants();
    });

    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      qvenariLogger.info('RoomEvent.ParticipantDisconnected', {
        participantIdentity: participant.identity,
        participantName: participant.name,
      }, 'Participant left the hunt meeting');
      syncParticipants();
    });

    room.on(RoomEvent.TrackPublished, (pub, participant) => {
      qvenariLogger.debug('RoomEvent.TrackPublished', {
        participantIdentity: participant.identity,
        trackSid: pub.trackSid,
        trackKind: pub.kind,
        trackSource: pub.source,
        isMuted: pub.isMuted,
      }, `Participant published ${pub.source} track`);
      syncParticipants();
    });

    room.on(RoomEvent.TrackUnpublished, (pub, participant) => {
      qvenariLogger.debug('RoomEvent.TrackUnpublished', {
        participantIdentity: participant.identity,
        trackSid: pub.trackSid,
        trackKind: pub.kind,
        trackSource: pub.source,
      }, `Participant unpublished ${pub.source} track`);
      syncParticipants();
    });

    room.on(RoomEvent.TrackMuted, (pub, participant) => {
      qvenariLogger.info('RoomEvent.TrackMuted', {
        participantIdentity: participant.identity,
        trackSid: pub.trackSid,
        trackKind: pub.kind,
        trackSource: pub.source,
      }, `Participant muted their ${pub.source}`);
      syncParticipants();
    });

    room.on(RoomEvent.TrackUnmuted, (pub, participant) => {
      qvenariLogger.info('RoomEvent.TrackUnmuted', {
        participantIdentity: participant.identity,
        trackSid: pub.trackSid,
        trackKind: pub.kind,
        trackSource: pub.source,
      }, `Participant unmuted their ${pub.source}`);
      syncParticipants();
    });

    room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
      qvenariLogger.debug('RoomEvent.TrackSubscribed', {
        participantIdentity: participant.identity,
        trackKind: track.kind,
        trackSid: pub.trackSid,
        trackSource: pub.source,
        isMuted: pub.isMuted,
      }, `Subscribed to ${pub.source} from participant`);

      if (track.kind === Track.Kind.Audio) {
        qvenariLogger.debug('Audio track attachment', {
          participantIdentity: participant.identity,
          speakerMuted: speakerMutedRef.current,
        }, 'Attaching remote audio element');

        const el = track.attach() as HTMLAudioElement;
        el.volume = 1;
        el.muted = speakerMutedRef.current;
        document.body.appendChild(el);
        audioElemsRef.current.set(participant.identity + '_a', el);
      }
      syncParticipants();
    });

    room.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
      qvenariLogger.debug('RoomEvent.TrackUnsubscribed', {
        participantIdentity: participant.identity,
        trackKind: track.kind,
        trackSid: pub.trackSid,
        trackSource: pub.source,
      }, `Unsubscribed from ${pub.source}`);

      track.detach();
      if (track.kind === Track.Kind.Audio) {
        const el = audioElemsRef.current.get(participant.identity + '_a');
        if (el) {
          qvenariLogger.debug('Audio element cleanup', {
            participantIdentity: participant.identity,
          }, 'Removing and stopping audio element');
          try { el.pause(); el.remove(); } catch {}
          audioElemsRef.current.delete(participant.identity + '_a');
        }
      }
      syncParticipants();
    });

    qvenariLogger.debug('Room.connect()', {
      livekitUrl: meeting.livekitUrl,
      tokenLength: meeting.token?.length,
      connectionState: room.state,
    }, 'Connecting to LiveKit server');

    // Re-attach self video whenever the local camera track is published (handles
    // LiveKit re-publishes after network hiccups or codec renegotiation).
    room.on(RoomEvent.LocalTrackPublished, (pub) => {
      qvenariLogger.info('LocalTrackPublished', {
        trackSource: pub.source,
        trackSid: pub.trackSid,
      }, 'Local track published — checking if camera needs re-attach');
      if (pub.source === Track.Source.Camera && pub.track && selfVideoElemRef.current) {
        qvenariLogger.info('LocalTrackPublished re-attach', { trackSid: pub.trackSid }, 'Re-attaching camera track to self-video element');
        pub.track.detach(selfVideoElemRef.current);
        pub.track.attach(selfVideoElemRef.current);
      }
    });

    room.connect(meeting.livekitUrl, meeting.token)
      .then(() => {
        qvenariLogger.debug('Room.connect() resolved', {
          connectionState: room.state,
          remoteParticipantCount: room.remoteParticipants.size,
        }, 'WebSocket connected — mic/cam OFF by default, user activates manually');
        // Mic and camera start OFF — user must press the buttons to enable them.
      })
      .catch(err => {
        qvenariLogger.error('Connection error', {
          livekitUrl: meeting.livekitUrl,
          errorMessage: err instanceof Error ? err.message : String(err),
        }, 'Failed to connect to LiveKit room');
      });

    return () => {
      qvenariLogger.debug('Cleanup', {
        meetingTitle: meeting.title,
        connectedAudioElements: audioElemsRef.current.size,
      }, 'Disconnecting from LiveKit and cleaning up audio elements');

      room.disconnect();
      audioElemsRef.current.forEach(el => { try { el.pause(); el.remove(); } catch {} });
      audioElemsRef.current.clear();

      qvenariLogger.info('Session ended', {
        meetingTitle: meeting.title,
      }, 'MapMeeting component cleanup complete');
    };
  }, [meeting.token, meeting.livekitUrl]);

  // ── Self video ──────────────────────────────────────────────────
  // Attachment is handled by the selfVideoRef callback-ref above.
  // No useEffect needed — callback fires the instant React mounts the <video> element.

  // ── Leader video ────────────────────────────────────────────────
  useEffect(() => {
    if (!leaderHasVideo || !leaderVideoRef.current || !roomRef.current) return;
    let attached = false;
    roomRef.current.remoteParticipants.forEach(p => {
      if (attached) return;
      const pub = p.getTrackPublication(Track.Source.Camera);
      if (pub?.track && leaderVideoRef.current) {
        pub.track.attach(leaderVideoRef.current);
        attached = true;
      }
    });
  }, [leaderHasVideo, participants]);

  // ── Selected participant video ──────────────────────────────────
  useEffect(() => {
    if (!selectedIdent || !selectedVideoRef.current || !roomRef.current) return;
    const p = roomRef.current.remoteParticipants.get(selectedIdent);
    if (!p) return;
    const pub = p.getTrackPublication(Track.Source.Camera);
    if (pub?.track) pub.track.attach(selectedVideoRef.current);
  }, [selectedIdent]);

  // ── Controls ────────────────────────────────────────────────────
  async function toggleMic() {
    qvenariLogger.info('toggleMic clicked', {
      roomReady: !!roomRef.current,
      roomState: roomRef.current?.state,
      currentMicEnabled: micEnabled,
      connectedState: connected,
    }, 'User clicked mic button');

    if (!roomRef.current) {
      qvenariLogger.error('toggleMic BLOCKED', { reason: 'roomRef.current is null' }, 'Room connection not ready');
      return;
    }
    const on = !micEnabled;

    qvenariLogger.info('toggleMic', {
      newState: on ? 'enabled' : 'muted',
      previousState: micEnabled ? 'enabled' : 'muted',
    }, `Participant ${on ? 'enabling' : 'muting'} microphone`);

    try {
      await roomRef.current.localParticipant.setMicrophoneEnabled(on);
      setMicEnabled(on);

      qvenariLogger.info('toggleMic result', {
        newState: on ? 'enabled' : 'muted',
        localParticipantIdentity: roomRef.current.localParticipant.identity,
      }, `Microphone ${on ? 'enabled — now transmitting' : 'muted'} successfully`);
    } catch (err) {
      // Keep state as-is if toggle fails (don't show wrong state in UI)
      qvenariLogger.error('toggleMic failed', {
        attemptedState: on ? 'enabled' : 'muted',
        errorMessage: err instanceof Error ? err.message : String(err),
        hint: 'getUserMedia requires HTTPS or localhost — check browser permissions',
      }, 'Failed to toggle microphone');
    }
  }

  async function toggleCam() {
    qvenariLogger.info('toggleCam clicked', {
      roomReady: !!roomRef.current,
      roomState: roomRef.current?.state,
      currentCamEnabled: camEnabled,
      connectedState: connected,
      camToggling: camTogglingRef.current,
    }, 'User clicked camera button');

    if (!roomRef.current || camTogglingRef.current) {
      if (camTogglingRef.current) {
        qvenariLogger.warn('toggleCam BLOCKED', { reason: 'Camera toggle already in progress' }, 'Rapid click detected');
      } else {
        qvenariLogger.error('toggleCam BLOCKED', { reason: 'roomRef.current is null' }, 'Room connection not ready');
      }
      return;
    }
    camTogglingRef.current = true;
    const on = !camEnabled;

    qvenariLogger.info('toggleCam', {
      newState: on ? 'enabled' : 'disabled',
      previousState: camEnabled ? 'enabled' : 'disabled',
    }, `Participant ${on ? 'enabling' : 'disabling'} camera`);

    try {
      const pub = await roomRef.current.localParticipant.setCameraEnabled(on);
      setCamEnabled(on);

      qvenariLogger.info('toggleCam result', {
        newState: on ? 'enabled' : 'disabled',
        permissionGranted: true,
        trackSid: pub?.trackSid,
      }, `Camera ${on ? 'enabled' : 'disabled'} successfully`);

      if (on) {
        // The selfVideoRef callback-ref will fire when React renders the <video> element.
        // But if the element is already in the DOM (e.g. hot reload), attach right now.
        if (pub?.track && selfVideoElemRef.current) {
          qvenariLogger.info('toggleCam — video element already mounted, attaching directly', {
            trackSid: pub.trackSid,
          });
          pub.track.detach(selfVideoElemRef.current);
          pub.track.attach(selfVideoElemRef.current);
        }
      } else {
        // Detach and clear srcObject when camera is turned off
        const el = selfVideoElemRef.current;
        if (el) {
          const offPub = roomRef.current.localParticipant.getTrackPublication(Track.Source.Camera);
          if (offPub?.track) {
            qvenariLogger.debug('toggleCam detach', { trackSid: offPub.trackSid }, 'Detaching camera track');
            offPub.track.detach(el);
          }
          el.srcObject = null;
        }
      }
    } catch (err) {
      qvenariLogger.error('toggleCam failed', {
        attemptedState: on ? 'enabled' : 'disabled',
        errorMessage: err instanceof Error ? err.message : String(err),
      }, 'Failed to toggle camera — state NOT changed');
      // Don't change state if camera toggle failed
    } finally {
      setTimeout(() => { camTogglingRef.current = false; }, 300);
    }
  }

  function toggleSpeaker() {
    qvenariLogger.info('toggleSpeaker clicked', {
      roomReady: !!roomRef.current,
      roomState: roomRef.current?.state,
      currentSpeakerMuted: speakerMuted,
      connectedState: connected,
      audioElementCount: audioElemsRef.current.size,
    }, 'User clicked speaker button');

    const muted = !speakerMuted;

    qvenariLogger.info('toggleSpeaker', {
      newState: muted ? 'muted' : 'unmuted',
      audioElementCount: audioElemsRef.current.size,
    }, `Participant ${muted ? 'muting' : 'unmuting'} speaker`);

    speakerMutedRef.current = muted;
    setSpeakerMuted(muted);
    audioElemsRef.current.forEach(el => { el.muted = muted; });

    qvenariLogger.debug('toggleSpeaker result', {
      newState: muted ? 'muted' : 'unmuted',
      appliedToCount: audioElemsRef.current.size,
    }, `Speaker ${muted ? 'muted' : 'unmuted'} for all audio elements`);
  }

  function handleEnd() {
    qvenariLogger.info('handleEnd clicked', {
      roomReady: !!roomRef.current,
      roomState: roomRef.current?.state,
      connectedState: connected,
    }, 'User clicked end meeting button');

    qvenariLogger.info('handleEnd', {
      meetingTitle: meeting.title,
      isLeader: meeting.isLeader,
    }, 'Ending hunt meeting and disconnecting from LiveKit');

    roomRef.current?.disconnect();

    qvenariLogger.info('handleEnd disconnected', {
      meetingTitle: meeting.title,
    }, 'Disconnected from LiveKit, calling onEnd callback');

    onEnd();

    qvenariLogger.info('handleEnd complete', {
      meetingTitle: meeting.title,
    }, 'Hunt meeting session terminated completely');
  }

  const pList = Array.from(participants.values());
  const selP = selectedIdent ? participants.get(selectedIdent) : null;

  return (
    <>
      {/* ── Jaktledare split-screen video (nedan kartan) ── */}
      {leaderHasVideo && (
        <div style={{
          width: '100%',
          height: fullscreen ? '40vh' : 240,
          background: '#000',
          position: 'relative',
          flexShrink: 0,
          borderTop: `2px solid ${T.amber}`,
        }}>
          <video
            ref={leaderVideoRef}
            autoPlay
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{
            position: 'absolute', top: 8, left: 12,
            color: T.amber, fontSize: 12, fontWeight: 700,
            background: 'rgba(0,0,0,0.65)', padding: '3px 8px', borderRadius: 4,
          }}>
            Jaktledare — video på
          </div>
        </div>
      )}

      {/* ── Deltagarlista (höger kant) ── */}
      {pList.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '40%',
          right: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 200,
        }}>
          {pList.map(p => (
            <button
              key={p.identity}
              onClick={() => setSelectedIdent(p.identity === selectedIdent ? null : p.identity)}
              title={`${p.name}${p.camOn ? ' — kamera på' : ''}`}
              style={{
                width: 44, height: 44, borderRadius: '50%', cursor: 'pointer',
                border: `2px solid ${p.camOn ? T.green : T.forest}`,
                background: selectedIdent === p.identity ? T.amber : T.surface,
                color: selectedIdent === p.identity ? '#000' : T.text,
                fontSize: 15, fontWeight: 700, padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
              }}
            >
              {p.name.charAt(0).toUpperCase()}
              {p.camOn && (
                <div style={{
                  position: 'absolute', top: -3, right: -3,
                  width: 11, height: 11, borderRadius: '50%',
                  background: T.green, border: '1px solid #000',
                }} />
              )}
              {!p.micOn && (
                <div style={{
                  position: 'absolute', bottom: -3, right: -3,
                  fontSize: 8, background: T.danger, color: '#fff',
                  borderRadius: 3, padding: '0 2px', lineHeight: '12px',
                }}>M</div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ── Självvy (nere till höger) ── */}
      {camEnabled && (
        <div style={{
          position: 'absolute',
          bottom: 64,
          right: pList.length > 0 ? 68 : 12,
          width: 110,
          height: 82,
          background: '#000',
          borderRadius: 6,
          overflow: 'hidden',
          border: `2px solid ${T.amber}`,
          zIndex: 200,
        }}>
          <video
            ref={selfVideoRef}
            autoPlay
            muted
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
          />
          <div style={{
            position: 'absolute', bottom: 2, left: 4,
            fontSize: 10, color: T.text, fontWeight: 700,
            background: 'rgba(0,0,0,0.5)', padding: '1px 4px', borderRadius: 2,
          }}>Du</div>
        </div>
      )}

      {/* ── Vald deltagares video ── */}
      {selectedIdent && (
        <div style={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300, height: 225,
          background: '#000',
          borderRadius: 8,
          overflow: 'hidden',
          border: `2px solid ${T.amber}`,
          zIndex: 350,
          boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
        }}>
          {selP?.camOn ? (
            <video
              ref={selectedVideoRef}
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: T.forest, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: 28, color: T.text, fontWeight: 700,
              }}>
                {selP?.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ color: T.textSub, fontSize: 13 }}>Kamera av</div>
            </div>
          )}
          <div style={{
            position: 'absolute', top: 8, left: 10,
            color: T.text, fontSize: 12, fontWeight: 700,
            background: 'rgba(0,0,0,0.65)', padding: '2px 8px', borderRadius: 4,
          }}>
            {selP?.name}
          </div>
          <button
            onClick={() => setSelectedIdent(null)}
            style={{
              position: 'absolute', top: 6, right: 6,
              background: 'rgba(0,0,0,0.7)', border: `1px solid ${T.forest}`,
              color: T.text, width: 26, height: 26, borderRadius: '50%',
              cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>
      )}

      {/* ── Kontrollrad längst ner ── */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        height: 56,
        background: 'rgba(10,26,8,0.92)',
        backdropFilter: 'blur(8px)',
        borderTop: `1px solid ${T.forest}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        zIndex: 200,
        padding: '0 14px',
      }}>
        {/* Statusindikator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginRight: 6, flexShrink: 0 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: connected ? T.green : '#ff4444',
            boxShadow: connected ? `0 0 6px ${T.green}` : undefined,
          }} />
          <span style={{
            color: T.text, fontSize: 11, fontWeight: 600,
            maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {meeting.title}
          </span>
          {pList.length > 0 && (
            <span style={{ color: T.textSub, fontSize: 10 }}>
              · {pList.length + 1}
            </span>
          )}
        </div>

        {/* Mikrofon */}
        <CtrlBtn
          active={micEnabled}
          danger={!micEnabled}
          title={micEnabled ? 'Tysta mic' : 'Slå på mic'}
          onClick={toggleMic}
        >
          {micEnabled ? '🎙' : '🔇'}
        </CtrlBtn>

        {/* Hörlur / Ljud */}
        <CtrlBtn
          active={!speakerMuted}
          danger={speakerMuted}
          title={speakerMuted ? 'Slå på ljud' : 'Tysta ljud'}
          onClick={toggleSpeaker}
        >
          {speakerMuted ? '🔕' : '🔊'}
        </CtrlBtn>

        {/* Kamera */}
        <CtrlBtn
          active={false}
          highlight={camEnabled}
          title={camEnabled ? 'Stäng kamera' : 'Slå på kamera'}
          onClick={toggleCam}
        >
          {camEnabled ? '📷' : '📷'}
        </CtrlBtn>

        {/* Avsluta */}
        <button
          onClick={handleEnd}
          title="Avsluta jakt"
          style={{
            width: 44, height: 44, borderRadius: '50%', cursor: 'pointer',
            border: `2px solid ${T.danger}`,
            background: 'rgba(229,57,53,0.18)',
            color: T.danger,
            fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginLeft: 8,
          }}
        >✕</button>
      </div>
    </>
  );
}

// ── Liten kontrollknapp ─────────────────────────────────────────
function CtrlBtn({
  children, active, danger, highlight, title, onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  danger?: boolean;
  highlight?: boolean;
  title: string;
  onClick: () => void;
}) {
  const borderColor = danger ? T.danger : highlight ? T.amber : active ? T.forest : T.forest;
  const bg = danger ? 'rgba(229,57,53,0.2)' : highlight ? 'rgba(238,143,9,0.2)' : T.surface;
  const color = danger ? T.danger : highlight ? T.amber : T.text;

  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 44, height: 44, borderRadius: '50%', cursor: 'pointer',
        border: `2px solid ${borderColor}`,
        background: bg, color,
        fontSize: 18,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  );
}
